import { Bell01, Mail01, MessageTextSquare01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CheckBox from 'src/components/CheckBox';
import CommonModal from 'src/components/CommonModal';
import InputText from 'src/components/InputText/InputText';
import PhoneInput from 'src/components/PhoneInput/PhoneInput';
import Toggle from 'src/components/Toggle/Toggle';
import { INotificationTypeSettings, INotificationTypes } from 'src/services/CustomerService';
import WalToast from 'src/utils/WalToast';

import { buildDeliveryPayload, IDeliveryAddresses, IDeliveryPayload, usesChannel } from './notificationDelivery';

interface IProps {
    handleClose: (saved: boolean) => void;
    existingData?: INotificationTypes | null;
    /** Per-order use only (cp-meeting-changes F2): shows the "Send to (this load only)" block, prefilled with these. */
    delivery?: IDeliveryAddresses;
    onSave: (notificationTypes: INotificationTypes, delivery?: IDeliveryPayload) => Promise<void>;
}

const NOTIFICATION_KEYS = ['location_updates', 'status_changes', 'document_uploads', 'order_modifications'] as const;
type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

const buildInitial = (existing?: INotificationTypes | null): INotificationTypes => {
    const defaults: Record<NotificationKey, INotificationTypeSettings> = {
        location_updates: { enabled: false, channels: { email: false, inApp: false, sms: false } },
        status_changes: { enabled: false, channels: { email: false, inApp: false, sms: false } },
        document_uploads: { enabled: false, channels: { email: false, inApp: false, sms: false } },
        order_modifications: { enabled: false, channels: { email: false, inApp: false, sms: false } },
    };

    if (!existing) return defaults;

    return {
        location_updates: existing.location_updates ?? defaults.location_updates,
        status_changes: existing.status_changes ?? defaults.status_changes,
        document_uploads: existing.document_uploads ?? defaults.document_uploads,
        order_modifications: existing.order_modifications ?? defaults.order_modifications,
    };
};

const NotificationSettingsModal: React.FC<IProps> = ({ handleClose, existingData, delivery, onSave }) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [notificationTypes, setNotificationTypes] = useState<INotificationTypes>(() => buildInitial(existingData));
    // Rows whose error is currently on screen. Populated ONLY by a failed Save and cleared the
    // moment that row is touched again — a row goes red because the customer asked to save it that
    // way, never while they are still mid-edit (turning a toggle on is briefly invalid by nature,
    // and flashing red at that instant would be nagging).
    const [submittedErrors, setSubmittedErrors] = useState<Set<NotificationKey>>(new Set());
    // Send to (this load only). A field shows only while an enabled event uses its channel, and goes
    // red only after a Save that found it empty or invalid, same as the rows above.
    const [deliveryEmail, setDeliveryEmail] = useState(delivery?.email ?? '');
    const [deliveryPhone, setDeliveryPhone] = useState(delivery?.phone ?? '');
    const [deliveryErrors, setDeliveryErrors] = useState({ email: false, phone: false });
    const showDeliveryEmail = !!delivery && usesChannel(notificationTypes, 'email');
    const showDeliveryPhone = !!delivery && usesChannel(notificationTypes, 'sms');

    // A row is invalid when its toggle is ON but no delivery channel is selected — the type would be
    // stored "on" with nowhere to deliver to, which the customer reads as notifications being active
    // while none can ever arrive.
    const getRowError = (key: NotificationKey): boolean => {
        const s = notificationTypes[key] as INotificationTypeSettings;

        return s.enabled && !Object.values(s.channels).some(Boolean);
    };

    const clearRowError = (key: NotificationKey) =>
        setSubmittedErrors((prev) => {
            if (!prev.has(key)) return prev;

            const next = new Set(prev);
            next.delete(key);

            return next;
        });

    const mainTypes: NotificationKey[] = ['location_updates', 'status_changes', 'document_uploads'];
    const otherTypes: NotificationKey[] = ['order_modifications'];

    const typeLabels: Record<NotificationKey, { title: string; desc: string }> = useMemo(
        () => ({
            location_updates: {
                title: t('Dashboard.NotificationSettings.Location_Updates'),
                desc: t('Dashboard.NotificationSettings.Location_Updates_Desc'),
            },
            status_changes: {
                title: t('Dashboard.NotificationSettings.Status_Changes'),
                desc: t('Dashboard.NotificationSettings.Status_Changes_Desc'),
            },
            document_uploads: {
                title: t('Dashboard.NotificationSettings.Document_Uploads'),
                desc: t('Dashboard.NotificationSettings.Document_Uploads_Desc'),
            },
            order_modifications: {
                title: t('Dashboard.NotificationSettings.Order_Modifications'),
                desc: t('Dashboard.NotificationSettings.Order_Modifications_Desc'),
            },
        }),
        [t]
    );

    const handleToggle = (key: NotificationKey) => {
        clearRowError(key);
        setNotificationTypes((prev) => ({
            ...prev,
            [key]: {
                ...(prev[key] as INotificationTypeSettings),
                enabled: !(prev[key] as INotificationTypeSettings).enabled,
            },
        }));
    };

    const handleChannel = (key: NotificationKey, channel: 'email' | 'inApp' | 'sms') => {
        clearRowError(key);
        setNotificationTypes((prev) => {
            const current = prev[key] as INotificationTypeSettings;
            const newChannelValue = !current.channels[channel];
            const newChannels = { ...current.channels, [channel]: newChannelValue };

            // Auto-enable toggle when any channel is checked.
            // Auto-disable toggle when all channels become unchecked.
            // Manual toggle clicks are unaffected (handled by handleToggle).
            let newEnabled = current.enabled;

            if (newChannelValue) {
                newEnabled = true;
            } else if (!Object.values(newChannels).some(Boolean)) {
                newEnabled = false;
            }

            return {
                ...prev,
                [key]: { ...current, enabled: newEnabled, channels: newChannels },
            };
        });
    };

    const handleSave = async () => {
        // Save stays clickable in every state — the button never greys out on the customer with no
        // explanation. Pressing it on an invalid configuration is what surfaces the red borders, and
        // nothing is sent until every enabled type has somewhere to deliver to.
        const errorKeys = NOTIFICATION_KEYS.filter(getRowError);

        if (errorKeys.length > 0) {
            setSubmittedErrors(new Set(errorKeys));

            return;
        }

        // Per-load Send to: errors keep the modal open with red borders; see buildDeliveryPayload.
        let deliveryPayload: IDeliveryPayload | undefined;

        if (delivery) {
            const built = buildDeliveryPayload({ email: deliveryEmail, phone: deliveryPhone, showEmail: showDeliveryEmail, showPhone: showDeliveryPhone }, delivery);

            if ('errors' in built) {
                setDeliveryErrors(built.errors);

                return;
            }

            deliveryPayload = built.payload;
        }

        setIsLoading(true);

        try {
            await onSave(notificationTypes, deliveryPayload);
            handleClose(true);
        } catch {
            WalToast.error(t('Dashboard.NotificationSettings.Failed_To_Save'));
        } finally {
            setIsLoading(false);
        }
    };

    const renderTypeRow = (key: NotificationKey) => {
        const settings = notificationTypes[key] as INotificationTypeSettings;
        const { title, desc } = typeLabels[key];

        const rowError = submittedErrors.has(key);

        return (
            <div key={key} className="flex flex-col gap-3 py-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-col">
                        <p className="text-[12px] leading-[18px] font-semibold text-textSecondary">{title}</p>
                        <p className="text-[12px] leading-[18px] font-normal text-grayLight600">{desc}</p>
                    </div>
                    <Toggle id={`toggle-${key}`} isChecked={settings.enabled} onChange={() => handleToggle(key)} />
                </div>

                <div className="flex items-center gap-3">
                    {(['email', 'inApp', 'sms'] as const).map((channel) => {
                        const labelMap: Record<string, string> = {
                            email: t('Dashboard.NotificationSettings.Email'),
                            inApp: t('Dashboard.NotificationSettings.InApp'),
                            sms: t('Dashboard.NotificationSettings.SMS'),
                        };
                        const iconMap: Record<string, React.ReactNode> = {
                            email: <Mail01 className={`w-4 h-4 text-gray500`} />,
                            inApp: <Bell01 className={`w-4 h-4 text-gray500`} />,
                            sms: <MessageTextSquare01 className={`w-4 h-4 text-gray500`} />,
                        };

                        return (
                            <label
                                key={channel}
                                className={`flex items-center min-w-[110px] gap-1.5 border rounded-md px-2 py-1.5 select-none ${
                                    settings.channels[channel] ? 'border-indigo300 bg-utilityIndigo25' : rowError ? 'border-danger500 bg-white' : 'border-borderPrimary bg-white'
                                } ${settings.enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                            >
                                {iconMap[channel]}
                                <span className={`text-xs font-medium flex-1 text-textSecondary`}>{labelMap[channel]}</span>
                                <CheckBox
                                    id={`${key}-${channel}`}
                                    name={`${key}-${channel}`}
                                    checked={settings.channels[channel]}
                                    onChangeFunc={() => settings.enabled && handleChannel(key, channel)}
                                    disabled={!settings.enabled}
                                    parentClassName="!mb-0"
                                />
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <CommonModal
            title={t('Dashboard.NotificationSettings.Title')}
            handleClose={() => handleClose(false)}
            size="max-w-[688px]"
            primaryBtnText={t('Common.Save')}
            primaryBtnOnClick={handleSave}
            primaryBtnLoading={isLoading}
            secondaryBtnText={t('Common.Cancel')}
            secondaryBtnOnClick={() => handleClose(false)}
        >
            <div className="py-4 px-4 flex flex-col gap-3">
                <div className="flex flex-col">
                    <p className="text-[12px] font-semibold mb-1.5 text-textSecondary leading-[18px]">{t('Dashboard.NotificationSettings.Select_Notification_Types')}</p>
                    <div className="flex flex-col border border-utilityGray200 rounded-[10px] bg-gray25 px-3 divide-y divide-utilityGray200">
                        {mainTypes.map(renderTypeRow)}
                        {otherTypes.map(renderTypeRow)}
                    </div>
                </div>

                {(showDeliveryEmail || showDeliveryPhone) && (
                    <div className="flex flex-col">
                        <p className="text-[12px] font-semibold mb-1.5 text-textSecondary leading-[18px]">{t('Dashboard.NotificationSettings.Send_To_Title')}</p>
                        <div className="flex flex-col gap-3 border border-utilityGray200 rounded-[10px] bg-gray25 p-3">
                            {showDeliveryEmail && (
                                <InputText
                                    label={t('Dashboard.NotificationSettings.Email_Address')}
                                    inputName="deliveryEmail"
                                    // Email type with the RFC length, not InputText's 50-character text default,
                                    // which silently drops what is typed past it.
                                    inputType="email"
                                    maxLength={254}
                                    value={deliveryEmail}
                                    required
                                    isError={deliveryErrors.email}
                                    onChangeFunc={(e: any) => {
                                        setDeliveryEmail(e.target.value);
                                        setDeliveryErrors((prev) => ({ ...prev, email: false }));
                                    }}
                                />
                            )}
                            {showDeliveryPhone && (
                                <PhoneInput
                                    label={t('Dashboard.NotificationSettings.Phone_Number')}
                                    required
                                    name="deliveryPhone"
                                    value={deliveryPhone}
                                    errors={deliveryErrors.phone}
                                    isShowErrorMsg={false}
                                    onNumberChange={(_country: any, number: string, code: string) => {
                                        setDeliveryPhone(number?.trim() ? `${code}${number}` : '');
                                        setDeliveryErrors((prev) => ({ ...prev, phone: false }));
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </CommonModal>
    );
};

export default NotificationSettingsModal;
